import type { EducationInquiry } from '../types/educationInquiry';

/**
 * Education Inquiry email template builder.
 *
 * Predictable, controlled template — not an AI/dynamic generator.
 * The only things that change are:
 *   - First name in greeting
 *   - Quantity-based pricing paragraph (or fallbacks for missing/very large quantities)
 *   - Teacher device line on/off
 *   - Timeline-aware follow-up question
 *
 * See EDUCATION_INQUIRIES_GUIDE.md and the source spec for the approved
 * base template and tone guidelines.
 */

export interface GeneratedEducationEmail {
  subject: string;
  body: string;
}

export interface GenerateEducationInquiryEmailOptions {
  inquiry: EducationInquiry;
  manualUnitPrice?: number;
  includeTeacherDeviceOffer?: boolean;
  subject?: string;
  /**
   * Optional staged quantity to use in place of `inquiry.estimated_quantity`.
   * Lets the drawer preview the email with an unsaved staff estimate before
   * the PATCH lands. `undefined` falls back to the saved value; explicit
   * `null` clears it.
   */
  effectiveQuantity?: number | null;
}

export const DEFAULT_SUBJECT = 'BYOK education pricing';

/**
 * Pricing tiers for the early education pilot.
 *
 *   1–4   units: $179 each
 *   5–9   units: $169 each
 *  10–19  units: $159 each
 *  20–49  units: $149 each
 *  50–99  units: $139 each
 * 100+    units: custom quote (returns null)
 */
export function getEducationUnitPrice(
  quantity: number | null | undefined,
): number | null {
  if (quantity == null || !Number.isFinite(quantity) || quantity <= 0) return null;
  if (quantity >= 100) return null;
  if (quantity >= 50) return 139;
  if (quantity >= 20) return 149;
  if (quantity >= 10) return 159;
  if (quantity >= 5) return 169;
  return 179;
}

/**
 * Pull a first name out of the educator's submitted full name.
 * Empty / missing → "there".
 */
export function getFirstName(name: string | null | undefined): string {
  if (!name) return 'there';
  const first = name.trim().split(/\s+/)[0];
  return first || 'there';
}

/**
 * Pricing paragraph variants:
 *  - missing quantity → ask for one before quoting
 *  - quantity >= 100  → custom-quote disclaimer
 *  - normal           → tier-based unit price
 */
export function buildPricingParagraph(
  quantity: number | null | undefined,
  unitPrice: number | null,
): string {
  if (quantity == null) {
    return (
      'Once we have a better sense of the number of devices you’re ' +
      'considering, we can put together education pricing. Shipping would ' +
      'be handled separately.'
    );
  }
  if (quantity >= 100 || unitPrice == null) {
    return (
      'Based on the size of the order you’re considering, this would likely ' +
      'be a custom quote. The final price would depend on quantity, ' +
      'accessory needs, timeline, and any purchasing requirements on your side.'
    );
  }
  return (
    `Based on your estimate of around ${quantity} devices, the base ` +
    `education price would likely be around $${unitPrice} per BYOK, with ` +
    'shipping handled separately.'
  );
}

/**
 * The optional teacher-device paragraph. Phrased loosely on purpose —
 * never a universal guarantee.
 */
export function buildTeacherDeviceParagraph(
  includeTeacherDeviceOffer: boolean,
): string {
  if (!includeTeacherDeviceOffer) return '';
  return (
    'For a classroom set around this size, we can likely include one ' +
    'teacher device at no additional cost.'
  );
}

/**
 * Timeline question variants:
 *  - submitted timeline → reference it, ask for a concrete date or term
 *  - blank              → fallback "is there a target timeline?" question
 */
export function buildTimelineQuestion(
  timeline: string | null | undefined,
): string {
  const trimmed = (timeline || '').trim();
  if (!trimmed) return 'Is there a target timeline you’re working around?';
  return (
    `I saw that your timeline is listed as “${trimmed}.” Is there a ` +
    'specific date or school term you’re hoping to have these by?'
  );
}

/**
 * Build the final {subject, body} pair from an inquiry and a few optional
 * overrides. Manual unit price wins over the tier-based price when supplied.
 *
 * The body matches the approved base template line-for-line; the only
 * variables interpolated are firstName, the pricing paragraph, the
 * (optional) teacher device line, and the timeline question.
 */
export function generateEducationInquiryEmail(
  options: GenerateEducationInquiryEmailOptions,
): GeneratedEducationEmail {
  const {
    inquiry,
    manualUnitPrice,
    includeTeacherDeviceOffer,
    subject,
    effectiveQuantity,
  } = options;

  const firstName = getFirstName(inquiry.name);
  const quantity =
    effectiveQuantity !== undefined ? effectiveQuantity : inquiry.estimated_quantity;
  const autoUnitPrice = getEducationUnitPrice(quantity);
  const finalUnitPrice =
    manualUnitPrice != null && Number.isFinite(manualUnitPrice) && manualUnitPrice > 0
      ? manualUnitPrice
      : autoUnitPrice;

  const pricingParagraph = buildPricingParagraph(quantity, finalUnitPrice);
  const teacherDeviceParagraph = buildTeacherDeviceParagraph(
    includeTeacherDeviceOffer === true,
  );
  const timelineQuestion = buildTimelineQuestion(inquiry.timeline);

  const accessoryParagraph =
    'We can also discuss a more complete classroom setup if helpful, ' +
    'including keyboards, carrying cases, and charging options. That would ' +
    'increase the total cost a bit depending on your needs, but our goal ' +
    'would be to keep the offer very competitive compared to other focused ' +
    'writing options.';

  // Build the body. Skip the teacher-device paragraph entirely when disabled
  // so we don't leave a blank-line gap.
  const bodyBlocks: string[] = [
    `Hi ${firstName},`,
    'Thank you for reaching out and for your interest in our new education program! We’re excited to work with educators early on as we build out this education program.',
    pricingParagraph,
    accessoryParagraph,
  ];
  if (teacherDeviceParagraph) bodyBlocks.push(teacherDeviceParagraph);
  bodyBlocks.push(
    'A few quick questions that would help us put together the right quote:\n\n' +
      '1. Would you be looking for BYOK devices only, or would keyboards, carrying cases, and/or charging options be helpful?\n' +
      `2. ${timelineQuestion}\n` +
      '3. Would you need a formal quote, invoice, or W-9 for the purchase process?',
    'Happy to help however I can.',
    'All the best,\nNick Sjolinder',
  );

  const body = bodyBlocks.join('\n\n');

  return {
    subject: (subject ?? '').trim() || DEFAULT_SUBJECT,
    body,
  };
}
